import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Info, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import PerformanceStatCards from '../components/performance-tracker/PerformanceStatCards';
import PerformanceControls from '../components/performance-tracker/PerformanceControls';
import PerformanceTable from '../components/performance-tracker/PerformanceTable';
import PerformanceDisclaimers from '../components/performance-tracker/PerformanceDisclaimers';
import PerformanceAlertsBanner from '../components/performance-tracker/PerformanceAlertsBanner';
import {
  PERFORMANCE_TABS,
  buildPerformanceCsv,
  computePerformanceStats,
  filterListings,
  formatAsOf,
  performanceTabByKey,
  selectListings,
} from '../utils/ipoPerformance';

// Matches the design: 17 listings per page
const PAGE_SIZE = 17;
const NOTICE_MS = 4500;

const EXCHANGES = {
  mainboard: 'BSE and NSE',
  sme: 'BSE SME and NSE Emerge',
};

// No endpoint anywhere in the backend tracks post-listing market price
// (DRHP/RHP extraction and the market-data service both stop at the IPO
// stage -- GMP and category-wise bids, never a listed stock's live quote),
// so this page has no real "issue price vs CMP" data to show. Rather than
// invent numbers, it stays on an honest empty state until a listed-price
// feed exists; LISTED_IPOS is kept empty and the current moment stands in
// for "as of" rather than a stale placeholder date.
const LISTED_IPOS = [];
const PRICES_AS_OF = new Date().toISOString().slice(0, 16);
const AS_OF = formatAsOf(PRICES_AS_OF);

export const IpoPerformancePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = performanceTabByKey(searchParams.get('tab'));
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const controlsRef = useRef(null);
  const tableRef = useRef(null);

  const heading = `${tab.noun} IPO Performance ${year}`;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${heading} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [heading]);

  useEffect(() => {
    setPage(1);
  }, [tab.key, year, filter, query]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const listings = useMemo(() => selectListings(LISTED_IPOS, { tab: tab.key, year }), [tab.key, year]);
  const stats = useMemo(() => computePerformanceStats(listings), [listings]);
  const rows = useMemo(() => filterListings(listings, { filter, query }), [listings, filter, query]);
  const tabCounts = useMemo(
    () =>
      Object.fromEntries(
        PERFORMANCE_TABS.map((option) => [option.key, selectListings(LISTED_IPOS, { tab: option.key, year }).length])
      ),
    [year]
  );

  const changeTab = (key) => setSearchParams(key === 'mainboard' ? {} : { tab: key }, { replace: true });

  const changePage = (nextPage) => {
    setPage(nextPage);
    // Paging from the bottom of a long table: bring its first row back into view
    if (tableRef.current && tableRef.current.getBoundingClientRect().top < 0) {
      tableRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read ₹ and dashes as UTF-8
    const blob = new Blob(['﻿', buildPerformanceCsv(rows, PRICES_AS_OF)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ipo-performance-${tab.key}-${year}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        {/* Partner ad (GET /api/ads): page slot first, then the home leaderboard */}
        <OfferStrip slotId={['ipo-performance-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
                IPO Reports
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-[#008080] font-semibold" aria-current="page">
                {tab.noun} IPO Performance Tracker {year}
              </span>
            </nav>

            <div className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-3 sm:gap-4">
              <div className="max-w-3xl">
                <h1 className="text-2xl sm:text-3xl lg:text-[34px] font-extrabold text-slate-900 tracking-tight leading-tight">
                  {heading}
                </h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Track real-time post-listing returns, listing day gains, and current profits of {tab.noun} IPO issues listed
                  on {EXCHANGES[tab.key]}. Monitor long-term wealth creation and stock price movements.
                </p>
              </div>
              <p className="self-start lg:self-auto lg:shrink-0 lg:whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-amber-200 bg-amber-50 shadow-sm text-[11px] sm:text-xs text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span>
                  Checked <strong className="font-bold">{AS_OF.short}</strong> — no live price feed connected yet
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 sm:px-5 py-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed">
              <strong className="font-bold">Listed-IPO price tracking isn't connected yet.</strong> This backend tracks
              DRHP/RHP extraction, grey market premium and live bidding, but not post-listing NSE/BSE quotes — so issue
              price vs. current market price can't be shown honestly right now. This page will populate automatically
              once a real listed-price feed is integrated.
            </p>
          </div>

          <PerformanceStatCards stats={stats} year={year} />

          <PerformanceControls
            ref={controlsRef}
            tab={tab.key}
            onTabChange={changeTab}
            tabCounts={tabCounts}
            year={year}
            years={[year]}
            onYearChange={setYear}
            onExport={handleExport}
            canExport={rows.length > 0}
            filter={filter}
            onFilterChange={setFilter}
            allCount={listings.length}
            query={query}
            onQueryChange={setQuery}
          />

          <PerformanceTable
            ref={tableRef}
            rows={rows}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={changePage}
            noun={tab.noun}
            onViewDetail={(row) =>
              setNotice(`The ${row.company} IPO detail page will open here once live performance data is connected.`)
            }
            onViewQuotes={(row) =>
              setNotice(`Live NSE / BSE quotes for ${row.company} will open here once market data is connected.`)
            }
            emptyMessage="No listed-price data is available yet — see the notice above."
          />

          <PerformanceDisclaimers asOf={AS_OF.long} />

          <PerformanceAlertsBanner eyebrow={`Upcoming ${tab.noun} Offerings`} onUnavailable={setNotice} />
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default IpoPerformancePage;
